import { FilterType } from 'bindings/filters/FilterType';
import { fieldTypeToFilterType } from '../../atoms/additionalFieldColumns';
import { Campaign, getCampaings } from '../../atoms/campaignAtom';
import { getFields } from '../../atoms/forms';
import { translateToString } from '../../styles/global/translate';
import type { Filter, FilterParameter } from './pages/FilterList';

// Company filters over linked promotions (backend Target::Promotions): the
// campaign picker, the period window, the "Campagnes actives" flag, the
// per-index period bounds (the same filters the promotions page offers), the
// EAN of a promoted product, and one filter per promotion custom field at the
// level that actually holds its values. A leaf module: usable from the
// companies page, the map, the report editor and the calculated fields editor
// without import cycles.
export async function generatePromotionFilters(): Promise<FilterParameter> {
	const [fields, promotions] = await Promise.all([getFields(), getCampaings().catch((): Campaign[] => [])]);
	// a field ticked both promotion and product is written once per
	// (promotion, product) link, so it filters through the link row; a
	// promotion-only field is written on the promotion itself. Offering both
	// kinds as a promotion-level filter is what made them match nothing
	const promotionFields = fields.filter(f => f.is_promotion && !f.is_product);
	const linkFields = fields.filter(f => f.is_promotion && f.is_product);
	// as many period slots as the widest promotion (the promotions page rule)
	const periodCount = promotions.reduce((acc, p) => Math.max(acc, p.periods.length), 1);
	// the backend has no money leaf: money fields filter as plain numbers
	const filterType = (type: Parameters<typeof fieldTypeToFilterType>[0]): FilterType => {
		const res = fieldTypeToFilterType(type);
		return (res as string) === 'money' ? 'numeric' : res;
	};
	return {
		category: translateToString('general.campaigns'),
		filters: [
			{ id: { promotions: 'campaign' }, name: translateToString('campaign'), type: 'campaign' },
			{ id: { promotions: 'period_active' }, name: translateToString('current_campagnes'), type: 'bool' },
			{ id: { promotions: 'period_date' }, name: translateToString('period'), type: 'temporal' },
			...Array.from({ length: periodCount }).flatMap((_, i): Filter[] => [
				{
					id: { promotions: { period_start: i } },
					name: `${translateToString('import.sidely_fields.start_date')} (${translateToString('period')} ${i + 1})`,
					type: 'temporal',
				},
				{
					id: { promotions: { period_end: i } },
					name: `${translateToString('import.sidely_fields.end_date')} (${translateToString('period')} ${i + 1})`,
					type: 'temporal',
				},
			]),
			// the EAN of a product the promotion carries: a hardcoded product
			// attribute, so it needs no field to have been set up
			{ id: { promotions: 'product_barcode' }, name: `${translateToString('barcode')} (${translateToString('campaigns.product')})`, type: 'string' },
			...promotionFields.map<Filter>(f => ({
				id: { promotions: { field: f.id } },
				name: `${f.name} (${translateToString('campaigns.campaign')})`,
				type: filterType(f.type),
			})),
			...linkFields.map<Filter>(f => ({
				id: { promotions: { product_field: f.id } },
				name: `${f.name} (${translateToString('campaigns.product')})`,
				type: filterType(f.type),
			})),
			// no period-level twin: nothing writes periods[].fields -- not the
			// popup, not the import, not the app -- so every such filter
			// matched nothing while reading as a second copy of the campaign
			// field. Target::Promotions still accepts period_field, so a view
			// saved with one keeps working
		],
	};
}
